import api from "../config/api";

import { Shipment } from "../models/Shipment";
import { ShipmentForm } from "../schemas/shipmentSchema";
import { ServicePaginatedReturn, ServiceReturn } from "../types/ServiceResponse";
import { UploadedFileReference } from "./uploadServices";

export interface GetShipmentsPayload {
  page?: number;
  perPage?: number;
  search?: string;
};

export interface ShipmentMutationPayload extends ShipmentForm {
  fileMetadata?: UploadedFileReference | null;
}

const moduleBaseUrl = "shipment";

export const saveShipment = async ( shipment: ShipmentMutationPayload, token?: string ): Promise<ServiceReturn<boolean>> => {
  try {
    const shouldUseMetadataOnly = Boolean(shipment.fileMetadata) && !shipment.file;

    const { data } = shouldUseMetadataOnly
      ? await api.post(`/${ moduleBaseUrl }`, {
          title: shipment.title,
          content: shipment.content,
          contactId: shipment.contact.id,
          sendDate: shipment.sendDate,
          sendTime: shipment.sendTime,
          shipmentMessageType: shipment.shipmentMessageType,
          fileId: shipment.fileMetadata?.fileId,
          storageKey: shipment.fileMetadata?.storageKey,
          thumbnailKey: shipment.fileMetadata?.thumbnailKey,
          mimeType: shipment.fileMetadata?.mimeType,
          fileName: shipment.fileMetadata?.fileName,
          originalFileName: shipment.fileMetadata?.originalFileName,
          size: shipment.fileMetadata?.size,
          extension: shipment.fileMetadata?.extension,
          sha256: shipment.fileMetadata?.sha256,
          durationSeconds: shipment.fileMetadata?.durationSeconds,
          pageCount: shipment.fileMetadata?.pageCount,
          width: shipment.fileMetadata?.width,
          height: shipment.fileMetadata?.height,
          uploadStatus: shipment.fileMetadata?.uploadStatus ?? "COMPLETED",
        }, {
          headers: {
            Authorization: `Bearer ${ token }`,
          }
        })
      : await (() => {
          const payload = new FormData();

          payload.append("title", shipment.title );
          payload.append("content", shipment.content );
          payload.append("contact", JSON.stringify( shipment.contact ));
          payload.append("sendDate", shipment.sendDate );

          if( shipment.shipmentMessageType ) payload.append("shipmentMessageType", shipment.shipmentMessageType );
          if( shipment.file ) payload.append("file", shipment.file );
          if( shipment.sendTime ) payload.append("sendTime", shipment.sendTime );

          return api.post(`/${ moduleBaseUrl }`, payload, {
            headers: {
              Authorization: `Bearer ${ token }`,
              'Content-Type': 'multipart/form-data',
            }
          });
        })();

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const getShipments = async ({ page = 1, perPage = 10, search }: GetShipmentsPayload, token?: string ): Promise<ServicePaginatedReturn<Shipment>> => {
  try {
    const {
      data
    } = await api.get(`/${ moduleBaseUrl }`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      },
      params: {
        page,
        perPage,
        search,
      }
    });

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const updateShipment = async ( shipment: ShipmentMutationPayload, token?: string ): Promise<ServiceReturn<boolean>> => {
  try {
    const shouldUseMetadataOnly = Boolean(shipment.fileMetadata) && !shipment.file;

    const { data } = shouldUseMetadataOnly
      ? await api.put(`/${ moduleBaseUrl }/${ shipment.id }`, {
          title: shipment.title,
          content: shipment.content,
          contactId: shipment.contact.id,
          sendDate: shipment.sendDate,
          sendTime: shipment.sendTime,
          shipmentMessageType: shipment.shipmentMessageType,
          fileId: shipment.fileMetadata?.fileId,
          storageKey: shipment.fileMetadata?.storageKey,
          thumbnailKey: shipment.fileMetadata?.thumbnailKey,
          mimeType: shipment.fileMetadata?.mimeType,
          fileName: shipment.fileMetadata?.fileName,
          originalFileName: shipment.fileMetadata?.originalFileName,
          size: shipment.fileMetadata?.size,
          extension: shipment.fileMetadata?.extension,
          sha256: shipment.fileMetadata?.sha256,
          durationSeconds: shipment.fileMetadata?.durationSeconds,
          pageCount: shipment.fileMetadata?.pageCount,
          width: shipment.fileMetadata?.width,
          height: shipment.fileMetadata?.height,
          uploadStatus: shipment.fileMetadata?.uploadStatus ?? "COMPLETED",
        }, {
          headers: {
            Authorization: `Bearer ${ token }`,
          }
        })
      : await (() => {
          const payload = new FormData();

          payload.append("title", shipment.title );
          payload.append("content", shipment.content );
          payload.append("contact", JSON.stringify( shipment.contact ));
          payload.append("sendDate", shipment.sendDate );

          if( shipment.shipmentMessageType ) payload.append("shipmentMessageType", shipment.shipmentMessageType );
          if( shipment.file ) payload.append("file", shipment.file );
          if( shipment.sendTime ) payload.append("sendTime", shipment.sendTime );

          return api.put(`/${ moduleBaseUrl }/${ shipment.id }`, payload, {
            headers: {
              Authorization: `Bearer ${ token }`,
              'Content-Type': 'multipart/form-data',
            }
          });
        })();

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};

export const deleteShipment = async ( id: string, token?: string ): Promise<ServiceReturn<boolean>> => {
  try {
    const {
      data
    } = await api.delete(`/${ moduleBaseUrl }/${ id }`, {
      headers: {
        Authorization: `Bearer ${ token }`,
      }
    });

    return data;
  } catch ( error: any ) {
    return {
      data: false,
      message: error?.response?.data?.message ?? error?.message,
    };
  }
};
